import { api, handleApiResponse, handleApiError } from '@/lib/api';
import { 
  Kanban, 
  CreateKanbanRequest, 
  UpdateKanbanRequest,
  KanbanEtapa,
  CreateKanbanEtapaRequest,
  UpdateKanbanEtapaRequest,
  UserKanban,
  CreateUserKanbanRequest,
  UpdateUserKanbanRequest
} from '@/types';

export const kanbanService = {
  // Métodos para Kanban
  async getAllKanbans(): Promise<Kanban[]> {
    try {
      const response = await api.get('/kanbans');
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getKanbanById(id: string): Promise<Kanban> {
    try {
      const response = await api.get(`/kanbans/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getDefaultKanban(): Promise<Kanban | null> {
    try {
      const response = await api.get('/kanbans/default');
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async createKanban(data: CreateKanbanRequest): Promise<Kanban> {
    try {
      const response = await api.post('/kanbans', data);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateKanban(id: string, data: UpdateKanbanRequest): Promise<Kanban> {
    try {
      const response = await api.put(`/kanbans/${id}`, data);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async deleteKanban(id: string): Promise<void> {
    try {
      const response = await api.delete(`/kanbans/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async toggleKanbanStatus(id: string): Promise<Kanban> {
    try {
      const response = await api.patch(`/kanbans/${id}/toggle`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  // Métodos para KanbanEtapa
  async getKanbanEtapas(kanbanId: string): Promise<KanbanEtapa[]> {
    try {
      const response = await api.get(`/kanbans/${kanbanId}/etapas`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getKanbanEtapaById(id: string): Promise<KanbanEtapa> {
    try {
      const response = await api.get(`/kanbans/etapas/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async createKanbanEtapa(data: CreateKanbanEtapaRequest): Promise<KanbanEtapa> {
    try {
      console.log('🔍 kanbanService.createKanbanEtapa chamado com:', data);
      console.log('🎯 URL:', `/kanbans/${data.kanbanId}/etapas`);
      
      const response = await api.post(`/kanbans/${data.kanbanId}/etapas`, data);
      console.log('✅ Resposta da API:', response.data);
      return handleApiResponse(response);
    } catch (error) {
      console.error('❌ Erro no kanbanService.createKanbanEtapa:', error);
      handleApiError(error);
      throw error;
    }
  },

  async updateKanbanEtapa(id: string, data: UpdateKanbanEtapaRequest): Promise<KanbanEtapa> {
    try {
      const response = await api.put(`/kanbans/etapas/${id}`, data);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async deleteKanbanEtapa(id: string): Promise<void> {
    try {
      const response = await api.delete(`/kanbans/etapas/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async toggleKanbanEtapaStatus(id: string): Promise<KanbanEtapa> {
    try {
      const response = await api.patch(`/kanbans/etapas/${id}/toggle`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },
};

export const userKanbanService = {
  async getAllUserKanbans(): Promise<UserKanban[]> {
    try {
      const response = await api.get('/user-kanbans');
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async createUserKanban(data: CreateUserKanbanRequest): Promise<UserKanban> {
    try {
      const response = await api.post('/user-kanbans', data);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getUserKanbans(userId: string): Promise<UserKanban[]> {
    try {
      const response = await api.get(`/user-kanbans/user/${userId}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getKanbanUsers(kanbanId: string): Promise<UserKanban[]> {
    try {
      const response = await api.get(`/user-kanbans/kanban/${kanbanId}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getUserKanbanById(id: string): Promise<UserKanban> {
    try {
      const response = await api.get(`/user-kanbans/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async updateUserKanban(id: string, data: UpdateUserKanbanRequest): Promise<UserKanban> {
    try {
      const response = await api.put(`/user-kanbans/${id}`, data);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async deleteUserKanban(id: string): Promise<void> {
    try {
      const response = await api.delete(`/user-kanbans/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getUserAccessibleKanbans(userId: string): Promise<UserKanban[]> {
    try {
      const response = await api.get(`/user-kanbans/user/${userId}/accessible`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async getUserAccessibleEtapas(userId: string, kanbanId: string): Promise<{ etapasIds: string[] }> {
    try {
      const response = await api.get(`/user-kanbans/user/${userId}/kanban/${kanbanId}/etapas`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },

  async canUserAccessEtapa(userId: string, kanbanId: string, etapaId: string): Promise<{ canAccess: boolean }> {
    try {
      const response = await api.get(`/user-kanbans/user/${userId}/kanban/${kanbanId}/etapa/${etapaId}/access`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  },
};
